import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Trash2 } from "lucide-react";

/** Full inline product editor for /admin/cms/products/:slug/edit. */
export default function CmsProductEdit() {
  const { slug } = useParams();
  const nav = useNavigate();
  const isNew = !slug;

  const [doc, setDoc] = useState({
    slug: "",
    common_name: "",
    scientific_name: "",
    category: "",
    sub_category: "",
    brand: "",
    description: "",
    featured: false,
    tags: [],
    allergens: [],
    health_benefits: [],
    nutrition: { calories: "", protein_g: "", fat_g: "", carbs_g: "", fiber_g: "", sugar_g: "", sodium_mg: "" },
    images: [],
    related_recipes: [],
    related_articles: [],
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const r = await http.get(`/food-items/${encodeURIComponent(slug)}`);
        setDoc({ ...doc, ...r.data });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [slug]);

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setNutri = (k, v) => setDoc((d) => ({ ...d, nutrition: { ...(d.nutrition || {}), [k]: v } }));

  const save = async () => {
    if (!doc.common_name) {
      toast.error("Common name is required");
      return;
    }
    setBusy(true);
    try {
      const body = { ...doc };
      // Coerce numeric nutrition fields.
      body.nutrition = Object.fromEntries(
        Object.entries(body.nutrition || {}).map(([k, v]) => [k, v === "" ? null : Number(v)])
          .filter(([, v]) => v !== null && !Number.isNaN(v)),
      );
      if (isNew) {
        const r = await http.post("/food-items", body);
        toast.success("Product created");
        nav(`/admin/cms/products/${encodeURIComponent(r.data.slug)}/edit`, { replace: true });
      } else {
        await http.patch(`/food-items/${encodeURIComponent(slug)}`, body);
        toast.success("Product saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${doc.common_name}"? This cannot be undone.`)) return;
    try {
      await http.delete(`/food-items/${encodeURIComponent(slug)}`);
      toast.success("Product deleted");
      nav("/admin/cms/products", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading product…</div>;

  return (
    <div className="max-w-4xl" data-testid="cms-product-edit">
      <Link to="/admin/cms/products" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to products
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isNew ? "New product" : doc.common_name || "Untitled product"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isNew ? "Create a catalog entry from scratch." : `Editing /food-pulse/items/${slug}`}
          </p>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="outline" onClick={remove} data-testid="cms-product-delete" className="border-rose-200 text-rose-600 hover:bg-rose-50">
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          )}
          <Button onClick={save} disabled={busy} data-testid="cms-product-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Save className="mr-1 h-4 w-4" /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Basics">
          <Field label="Common name" required>
            <Input data-testid="cms-product-name" value={doc.common_name || ""} onChange={(e) => set("common_name", e.target.value)} />
          </Field>
          <Field label="Scientific name">
            <Input value={doc.scientific_name || ""} onChange={(e) => set("scientific_name", e.target.value)} />
          </Field>
          <Field label="Brand">
            <Input value={doc.brand || ""} onChange={(e) => set("brand", e.target.value)} />
          </Field>
          <Field label="Category">
            <Input data-testid="cms-product-category" value={doc.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="fruits, vegetables, seafood…" />
          </Field>
          <Field label="Subcategory">
            <Input value={doc.sub_category || ""} onChange={(e) => set("sub_category", e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea rows={4} value={doc.description || ""} onChange={(e) => set("description", e.target.value)} />
          </Field>
          <Field label="Featured on homepage">
            <div className="flex items-center gap-2">
              <Switch checked={!!doc.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="cms-product-featured" />
              <span className="text-xs text-slate-500">Only enable for editorial picks</span>
            </div>
          </Field>
        </Section>

        <Section title="Nutrition (per 100 g)">
          {[
            ["calories", "Calories"], ["protein_g", "Protein (g)"], ["fat_g", "Fat (g)"],
            ["carbs_g", "Carbs (g)"], ["fiber_g", "Fiber (g)"], ["sugar_g", "Sugar (g)"], ["sodium_mg", "Sodium (mg)"],
          ].map(([k, l]) => (
            <Field key={k} label={l}>
              <Input
                data-testid={`cms-product-nutri-${k}`}
                type="number"
                value={doc.nutrition?.[k] ?? ""}
                onChange={(e) => setNutri(k, e.target.value)}
              />
            </Field>
          ))}
        </Section>

        <Section title="Tags, allergens, health benefits">
          <TagField label="Tags" value={doc.tags} onChange={(v) => set("tags", v)} testid="cms-product-tags" />
          <TagField label="Allergens" value={doc.allergens} onChange={(v) => set("allergens", v)} testid="cms-product-allergens" />
          <TagField label="Health benefits" value={doc.health_benefits} onChange={(v) => set("health_benefits", v)} testid="cms-product-health" />
        </Section>

        <Section title="Related content">
          <TagField label="Related recipes (slugs)" value={doc.related_recipes} onChange={(v) => set("related_recipes", v)} testid="cms-product-recipes" />
          <TagField label="Related articles (slugs)" value={doc.related_articles} onChange={(v) => set("related_articles", v)} testid="cms-product-articles" />
          <TagField label="Image URLs" value={doc.images} onChange={(v) => set("images", v)} testid="cms-product-images" />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-slate-600">{label}{required && <span className="text-rose-600"> *</span>}</Label>
      {children}
    </div>
  );
}

function TagField({ label, value, onChange, testid }) {
  // Backend fields can arrive as null / undefined / string / array-of-objects
  // (images use {url, caption, credit, source}). Coerce to a stable array so
  // .map never blows up, and render objects via a friendly text projection.
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...list, v]);
    setDraft("");
  };
  const labelOf = (t) => {
    if (typeof t === "string") return t;
    if (t && typeof t === "object") return t.url || t.text || t.slug || t.name || JSON.stringify(t);
    return String(t);
  };
  return (
    <div>
      <Label className="mb-1 block text-xs text-slate-600">{label}</Label>
      <div className="flex flex-wrap items-center gap-1">
        {list.map((t, i) => (
          <button
            key={`${labelOf(t)}-${i}`}
            type="button"
            onClick={() => onChange(list.filter((_, j) => j !== i))}
            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            title="Click to remove"
          >
            {labelOf(t)} ×
          </button>
        ))}
        <div className="flex items-center gap-1">
          <Input
            data-testid={testid}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); add(); }
            }}
            className="h-7 w-40 text-xs"
            placeholder="Add + Enter"
          />
        </div>
      </div>
    </div>
  );
}
